import groq from "groq";
import { defineQuery } from "next-sanity";

export const ASSET_SCHEME = groq`
{
  ...
  // _id,
  // assetId,
  // sha1hash,
  // assetId,
  // _type,
  // path,
  // _rev,
  // url,
  // metadata: {
  //   lqip,
  //   blurHash,
  //   dimensions: {
  //     aspectRatio,
  //     height,
  //     width:1980
  //   }
    
  // }
}
`;

export const CELEBRITY_SCHEME = `
{
  ...,
  "about": coalesce(about[$lang], about.en, "Missing translation"),
}
`;

export const VENUE_SCHEME = `
{
  ...
}
`;

export const NEWS_SIGNAL_SCHEME = `
{
  ...,
  "title": coalesce(title[$lang], title.en, "Missing translation"),
}
`;

export const FAQ_SECTION_SCHEME = `
{
  ...,
  "slug": slug.current,
  "title": title[$lang],
  "questions": questions[] {
    ...,
    "question": coalesce(question[$lang], question.en, "Missing translation"),
    "answer": coalesce(answer[$lang], answer.en, "Missing translation")
  }
}
`;

export const RELATED_AREA_SCHEME = `
{
  ...,
  "body": coalesce(body[$lang], body.en, "Missing translation"),
  "slug": slug.current,
}
`;

export const AREA_SCHEME = `
{
  ...,
  "body": coalesce(body[$lang], body.en, "Missing translation"),
  "slug": slug.current,
  "headline": coalesce(headline[$lang], headline.en, "Missing translation"),
  "relatedAreas": relatedAreas[]->${RELATED_AREA_SCHEME},
  "videos": coalesce(videos[][$lang], videos[]["en"]),
  historicalImages[] {
    ...,
    "filename": asset->originalFilename
  }
}
`;

export const PAGE_SCHEME = `
{
  "title": coalesce(title[$lang], title.en, "Missing translation"),
  "body": coalesce(body[$lang], body.en, "Missing translation"),
  "slug": slug.current
}
`;

export const DEVELOPER_SCHEME = `
{
  ...,
  "body": coalesce(body[$lang], body.en, "Missing translation"),
  "slug": slug.current,
  "projectsCount": count(*[_type=='project' && references(^._id)]),
  "projectsInProgressCount": count(*[_type=='project' && references(^._id) && status == "started"]),
  "projectsFinishedCount": count(*[_type=='project' && references(^._id) && status == "completed"]),
  "projectsInPrelaunchCount": count(*[_type=='project' && references(^._id) && status == "prelaunch"])
}
`;

export const PROJECT_SCHEME = `
{
  ...,
  "teaser": coalesce(teaser[$lang], teaser.en, "Missing translation"),
  "name": coalesce(name[$lang], name.en, "Missing translation"),
  "intro": coalesce(intro[$lang], intro.en, "Missing translation"),
  "pdfIntro": coalesce(pdfIntro[$lang], pdfIntro.en, "Missing translation"),
  "body": coalesce(body[$lang], body.en, "Missing translation"),
  // "developer": developer->name,
  "developer": developer->${DEVELOPER_SCHEME},
  "area": area->${AREA_SCHEME},
  "slug": slug.current,
  "updatedAt": _updatedAt,
  "photos": photos[] {
    _type == 'image' => @,
    _type == 'localeFile' => @ {
      _key,
      _type,
      "asset": coalesce(@[$lang].asset, @.en.asset)->
    },
  },
  "floatingVideos": floatingVideos[] {
    _type != 'localeFile' => @,
    _type == 'localeFile' => @ {
      _key,
      _type,
      "asset": coalesce(@[$lang].asset, @.en.asset)->
    },
  },
  "amenities": amenities[] {
    ...,
    "title": coalesce(title[$lang], title.en, "Missing translation"),
    "caption": coalesce(caption[$lang], caption.en, "Missing translation")
  },
  "distances": distances[]{
    _type == 'venue' => @->${VENUE_SCHEME},
    _type != 'venue' => @,
  }, 
  "areas": select(
    area != null => [area->${AREA_SCHEME}, ...area->relatedAreas[]->${AREA_SCHEME}]
  ),
  "paymentPlans": paymentPlans[] {
    _key,
    priceModifier,
    "name": coalesce(name[$lang], name.en, "Missing translation"),
    "installments": installments[] {
      ...,
      "customConditionDescription": coalesce(customConditionDescription[$lang], customConditionDescription.en, "Missing translation")
    }
  },
  "virtualTours": virtualTours[],
  "floorPlans": floorPlans[] {
    ...
  },
  "promotions": promotions[] {
    ...,
    "title": coalesce(title[$lang], title.en),
    "description": coalesce(description[$lang], description.en)
  }
}
`;

export const LISTING_SCHEME = groq`
{
  // ...,
  size,
  "id": _id,
  "slug": slug.current,
  "title": coalesce(title[$lang], title.en, "Missing translation"),
  "description": coalesce(description[$lang], description.en, "Missing translation"),
  "number_of_bedrooms": numberOfBedrooms,
  "number_of_bathrooms": numberOfBathrooms,
  "asking_sales_price": askingSalesPrice,
  "original_price": originalPrice,
  "project": project->${PROJECT_SCHEME}
}
`;

export const AUTHOR_SCHEME = `
{
  ...
}
`;

export const INSIGHTS_ARTICLE_SCHEME = `
{
  ...,
  "translations": *[_type == "translation.metadata" && references(^._id)].translations[].value -> {
    language,
    "slug": slug.current,
  },
  "slug": slug.current,
  "author": author->${AUTHOR_SCHEME},
  "numberOfCharacters": length(pt::text(body)),
  "updatedAt": _updatedAt,
  // https://github.com/mxmzb/rentingtoriches/blob/fd159c9928499f028ece5b298d1b6949194eb164/packages/rentingtoriches-web/src/groq/queries.ts#L23
  "estimatedReadingTime": round(length(pt::text(body)) / 5 / 240)
}
`;

export const allCelebritiesQuery = ({ hitsPerPage = null }) => groq`
  *[_type == "celebrity" && defined(photo)] ${CELEBRITY_SCHEME} ${hitsPerPage ? `[0...${hitsPerPage}]` : ""}
`;

export const countProjectsQuery = groq`
count(*[_type == "project"])
`;

// https://www.sanity.io/docs/configuration
export const allProjectsQuery = ({ hitsPerPage = null }) => groq`
  *[_type == "project"] ${PROJECT_SCHEME} ${hitsPerPage ? `[0...${hitsPerPage}]` : ""}
`;

export const allProjectStatusesQuery = ({ page = 0, size = 10 }) => groq`
*[_type == "project"] {
  _id,
  "slug": slug.current,
  "name": name.en,
  "developerName": developer->name,
  "translations": {
    "intro": {
      "en": defined(intro.en), 
      "de": defined(intro.de),
      "ru": defined(intro.ru)
    },
    "area": {
      "en": defined(area->body.en),
      "de": defined(area->body.de), 
      "ru": defined(area->body.ru)
    },
    "developer": {
      "en": defined(developer->body.en),
      "de": defined(developer->body.de),
      "ru": defined(developer->body.ru)
    },
  },
  "hasPaymentPlansDefined": defined(paymentPlans) && count(paymentPlans) > 0,
  "hasAmenitiesDefined": defined(amenities) && count(amenities) > 0,
  "hasServiceChargesDefined": defined(serviceCharge.min) && defined(serviceCharge.max),
  "area": area->{
    "hasPhotosDefined": defined(photos) && count(photos) > 0,
  },
  "developer": developer->{
    "hasPhotosDefined": defined(poster),
  },
  "hasLocationDefined": defined(location.lat) && defined(location.lng),
  "hasDistancesDefined": defined(location.lat) && count(distances) > 3,
  "hasLaunchDefined": defined(launch),
  "hasCompletionDefined": defined(completion),
  "hasStatusDefined": defined(status),
  "hasStrAllowedDefined": defined(isStrAllowed),
  "hasOwnershipDefined": defined(ownership),
  "hasAppliancesDefined": defined(hasAppliances),
  "hasBuildingConfigurationDefined": defined(buildingConfiguration),
  "hasFittedDefined": defined(isFitted),
  "hasFurnishedDefined": defined(isFurnished),
  "createdAt": _createdAt
}[${page * size}...${(page + 1) * size}] | order(createdAt desc) 
`;

export const projectsByStatusQuery = ({ hitsPerPage = null }) => groq`
  *[_type == "project" && status == $status] ${PROJECT_SCHEME} ${
    hitsPerPage ? `[0...${hitsPerPage}]` : ""
  }
`;

export const validProjectsQuery = ({ hitsPerPage = null }) => groq`
  *[_type == "project" && status != "prelaunch"] ${PROJECT_SCHEME} ${
    hitsPerPage ? `[0...${hitsPerPage}]` : ""
  }
`;

export const validProjectsByDeveloperQuery = ({ hitsPerPage = null }) => groq`
  *[_type == "project" && status != "prelaunch" && developer._ref in *[_type == "developer" && slug.current == $developer]._id] ${PROJECT_SCHEME} ${
    hitsPerPage ? `[0...${hitsPerPage}]` : ""
  }
`;

export const validProjectsByAreaQuery = ({ hitsPerPage = null }) => groq`
  *[_type == "project" && status != "prelaunch" && developer._ref in *[_type == "developer" && slug.current == $developer]._id] ${PROJECT_SCHEME} ${
    hitsPerPage ? `[0...${hitsPerPage}]` : ""
  }
`;

export const projectQuery = groq`
*[_type == "project" && slug.current == $slug][0] ${PROJECT_SCHEME}
`;

export const projectByIdQuery = groq`
  *[_type == "project" && _id == $id][0] ${PROJECT_SCHEME}
`;

export const projectBySlugQuery = groq`
  *[_type == "project" && slug.current == $slug][0] ${PROJECT_SCHEME}
`;

export const insightsArticleQuery = groq`
  *[_type == "insightsArticle" && slug.current == $slug] [0] ${INSIGHTS_ARTICLE_SCHEME}
`;

export const allInsightsArticlesQuery = groq`
  *[_type == "insightsArticle" && language == $lang] | order(publishedAt desc) ${INSIGHTS_ARTICLE_SCHEME}
`;

export const consultationStatus = groq`
  *[_type == "siteSettings"][0].instantConsultation
`;

export const marketingPopup = groq`
*[_type == "siteSettings"][0].marketingPopup {
  ...,
  "title": coalesce(title[$lang], title.en, "Missing translation"),
  "body": coalesce(body[$lang], body.en, "Missing translation"),
}
`;

export const pageBySlugQuery = groq`
  *[_type == "page" && slug.current == $slug][0] ${PAGE_SCHEME}
`;

export const faqSectionQuery = groq`
  *[_type == "faq"][0].sections[slug.current == $slug][0] ${FAQ_SECTION_SCHEME}
`;

export const allFaqSectionsQuery = groq`
  *[_type == "faq"][0].sections[] ${FAQ_SECTION_SCHEME}
`;

export const allDeveloperLogosQuery = groq`
  *[_type == "developer" && defined(logo)] {
    logo,
    "developer": name
  }
`;

export const allDevelopersQuery = groq`
*[_type == "developer"] ${DEVELOPER_SCHEME}
`;

export const developerBySlugQuery = groq`
  *[_type == "developer" && slug.current == $slug][0] ${DEVELOPER_SCHEME}
`;

export const developerQuery = groq`
  *[_type == "developer" && _id == $id][0] ${DEVELOPER_SCHEME}
`;

export const allAreasQuery = groq`
  *[_type == "area"] ${AREA_SCHEME}
`;

export const allNewsSignalsQuery = groq`
  *[_type == "newsSignal"] | order(publishedAt desc) ${NEWS_SIGNAL_SCHEME}
`;

export const assetQuery = groq`
*[_type in ["sanity.fileAsset", "sanity.imageAsset" ] && !(_id in path("drafts.**")) && assetId == $id][0]
`;

export const areaQuery = groq`
  *[_type == "area" && _id == $id][0] ${AREA_SCHEME}
`;

export const areaBySlugQuery = groq`
  *[_type == "area" && slug.current == $slug][0] ${AREA_SCHEME}
`;

export const allIndexableProjectsSlugs = groq`
*[_type == "project" && slug.current != null && count(photos) > 0 && defined(location)].slug.current
`;

export const allIndexableProjects = groq`
*[_type == "project" && slug.current != null && count(photos) > 0 && defined(location)] ${PROJECT_SCHEME}
`;

export const allListingsQuery = groq`
*[_type == "listing"] ${LISTING_SCHEME}
`;

export const listingQuery = groq`
*[_type == "listing" && _id == $id][0] ${LISTING_SCHEME}
`;
